import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress} from '@mui/material';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';

import { authService } from '@/services/authService';
import { initializePreferencesFromUser } from '@/store/slices/preferencesSlice';
import { refreshCurrentUserAsync } from '@/store/slices/authSlice';
import type { AppDispatch } from '@/store';
import { useIdentifyUser } from '@/hooks/useIdentifyUser';

// Components
import ThemeProviderWrapper from '@/components/theme/ThemeProviderWrapper';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import { GlobalErrorBoundary } from '@/components/common/ErrorBoundary';
import PWAUpdatePrompt from '@/components/common/PWAUpdatePrompt';
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt';
import HeatmapConsentBanner from '@/components/common/HeatmapConsentBanner';

// Layouts
import FarmSelectorLayout from '@/components/layout/FarmSelectorLayout';
import FarmDashboardLayout from '@/components/layout/FarmDashboardLayout';

// Lazy-loaded page components
const Login = lazy(() => import('@/pages/auth/Login'));
const Signup = lazy(() => import('@/pages/auth/Signup'));
const EmailConfirmation = lazy(() => import('@/pages/auth/EmailConfirmation'));
const RequestPasswordReset = lazy(() => import('@/pages/auth/RequestPasswordReset'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const OAuthCallback = lazy(() => import('@/pages/auth/OAuthCallback'));
const ViewerAuthorize = lazy(() => import('@/pages/viewer/ViewerAuthorize'));
const AcceptInvitation = lazy(() => import('@/pages/invitations/AcceptInvitation'));
const ScheduleManagement = lazy(() => import('@/pages/schedules/ScheduleManagement'));
const ConstraintBuilderPage = lazy(() => import('@/pages/schedules/ConstraintBuilderPage'));
const ZoneSchedulingModePage = lazy(() => import('@/pages/schedules/ZoneSchedulingModePage'));
const Profile = lazy(() => import('@/pages/user/Profile'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const ValveNodeList = lazy(() => import('@/pages/valvenodes/ValveNodeList'));
const FarmSelectorPage = lazy(() => import('@/pages/farms/FarmSelectorPage'));
const FarmDashboardHome = lazy(() => import('@/pages/farms/FarmDashboardHome'));
const FarmLayoutEditor = lazy(() => import('@/components/farms/layout-editor/FarmLayoutEditor'));
const WaterBalanceDashboard = lazy(() => import('@/pages/waterBalance/WaterBalanceDashboard'));
const SeasonManagement = lazy(() => import('@/components/irrigationSeason/SeasonManagement'));
const WeatherPage = lazy(() => import('@/pages/farms/WeatherPage'));
const FarmMapPage = lazy(() => import('@/pages/farms/FarmMapPage'));
const FarmCreateWizardPage = lazy(() => import('@/pages/farms/FarmCreateWizardPage'));
const FarmSensorsPage = lazy(() => import('@/pages/farms/FarmSensorsPage'));
const ControllersPage = lazy(() => import('@/pages/farms/ControllersPage'));
const ControllerCockpitPage = lazy(() => import('@/pages/farms/ControllerCockpitPage'));
const CabezalPage = lazy(() => import('@/pages/farms/CabezalPage'));
const ProgramListPage = lazy(() => import('@/pages/programs/ProgramListPage'));
const IrrigationLogPage = lazy(() => import('@/pages/farms/IrrigationLogPage'));
const NotificationSettingsPage = lazy(() => import('@/pages/settings/NotificationSettingsPage'));
const AlertsPage = lazy(() => import('@/pages/farms/AlertsPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      width: '100%'
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

/**
 * Where to land when an auth page turns out to be reachable by a signed-in user.
 *
 * Reloading a deep page can bounce through /login before the session is known:
 * under cookie auth `isAuthenticated()` is false until GET /auth/me answers, and
 * a refresh that loses a token-rotation race fails its first attempt. This route
 * used to be a flat `<Navigate to="/" />`, so every such bounce ended at the farm
 * selector and the page you had open was gone. ProtectedRoute records the
 * location it redirected from; this returns you to it.
 */
const RedirectAfterAuth: React.FC = () => {
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  // Never bounce back to an auth page — that would loop through here forever.
  const target = from && !from.startsWith('/login') && !from.startsWith('/signup')
    ? from
    : '/';
  return <Navigate to={target} replace />;
};

// Wrapper component for SeasonManagement to extract farmId from URL params
const SeasonManagementWrapper: React.FC = () => {
  const { farmId } = useParams<{ farmId: string }>();
  if (!farmId) {
    return <Navigate to="/farms" replace />;
  }
  return <SeasonManagement farmId={farmId} />;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    authService.isAuthenticated()
  );
  // Cookie mode: false until GET /auth/me resolves. The gates below that are NOT
  // inside ProtectedRoute (the auth pages and the catch-all) must not decide
  // before then — deciding on a not-yet-known session is what sent a reload to
  // /login and from there to the farm selector.
  const [bootstrapped, setBootstrapped] = useState(() =>
    authService.isSessionBootstrapped()
  );

  // Associate the Umami analytics session with the logged-in user
  useIdentifyUser();

  useEffect(() => {
    // Cookie mode: discover the session via GET /auth/me (the httpOnly cookie
    // is sent automatically). Emits authStateChanged when it resolves, which
    // the handler below picks up. No-op in dual mode.
    void authService.bootstrapSession();

    if (authService.isAuthenticated()) {
      dispatch(initializePreferencesFromUser());
      dispatch(refreshCurrentUserAsync());
    }

    const handleAuthStateChange = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      // Set explicitly rather than relying on the isAuthenticated flip: a
      // bootstrap that resolves to "not signed in" leaves that value unchanged,
      // and the gates waiting on this would spin forever.
      setBootstrapped(authService.isSessionBootstrapped());

      if (authenticated) {
        dispatch(initializePreferencesFromUser());
        dispatch(refreshCurrentUserAsync());
      }
    };

    // After a token refresh (e.g. recovering from an expired access token),
    // re-sync the user profile so UI state like the avatar can't be left stale.
    const handleTokensRefreshed = () => {
      if (authService.isAuthenticated()) {
        dispatch(refreshCurrentUserAsync());
      }
    };

    authService.addEventListener('authStateChanged', handleAuthStateChange);
    authService.addEventListener('tokensRefreshed', handleTokensRefreshed);

    return () => {
      authService.removeEventListener('authStateChanged', handleAuthStateChange);
      authService.removeEventListener('tokensRefreshed', handleTokensRefreshed);
    };
  }, [dispatch]);

  return (
    <GlobalErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Global App Error:', {
          error: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        });
      }}
    >
      <ThemeProviderWrapper>
      <PWAInstallPrompt>
        <Router>
          <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  !bootstrapped ? <LoadingFallback /> :
                  isAuthenticated ?
                    <RedirectAfterAuth /> :
                    <Suspense fallback={<LoadingFallback />}>
                      <Login />
                    </Suspense>
                }
              />
              <Route
                path="/signup"
                element={
                  !bootstrapped ? <LoadingFallback /> :
                  isAuthenticated ?
                    <RedirectAfterAuth /> :
                    <Suspense fallback={<LoadingFallback />}>
                      <Signup />
                    </Suspense>
                }
              />
              <Route
                path="/confirm-email"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <EmailConfirmation />
                  </Suspense>
                }
              />
              <Route
                path="/request-password-reset"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <RequestPasswordReset />
                  </Suspense>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ResetPassword />
                  </Suspense>
                }
              />
              <Route
                path="/auth/callback/google"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <OAuthCallback />
                  </Suspense>
                }
              />
              <Route
                path="/invitations/accept"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AcceptInvitation />
                  </Suspense>
                }
              />

              {/* LiDAR viewer login hand-off (docs/viewer-integration/) */}
              <Route
                path="/viewer/authorize"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <ViewerAuthorize />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Farm Selector routes (Layout A - no sidebar) */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <FarmSelectorPage />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms"
                element={
                  <ProtectedRoute>
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <FarmSelectorPage />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />

              {/* Global pages in FarmSelectorLayout */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <Profile />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AdminDashboard />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={<Navigate to="/admin/dashboard?tab=users" replace />}
              />

              {/* Legacy routes in FarmSelectorLayout */}
              <Route path="/devices" element={<Navigate to="/valvenodes" replace />} />
              <Route
                path="/valvenodes"
                element={
                  <ProtectedRoute>
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ValveNodeList />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schedules"
                element={
                  <ProtectedRoute>
                    <FarmSelectorLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ScheduleManagement />
                      </Suspense>
                    </FarmSelectorLayout>
                  </ProtectedRoute>
                }
              />

              {/* Farm Create Wizard - fullscreen, no layout wrapper */}
              <Route
                path="/farms/create"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <FarmCreateWizardPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Farm Layout Editor - fullscreen, no layout wrapper */}
              <Route
                path="/farms/:farmId/layout"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <FarmLayoutEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Farm-scoped routes (Layout B - sidebar + top bar) */}
              <Route
                path="/farms/:farmId/dashboard"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <FarmDashboardHome />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/water-balance"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <WaterBalanceDashboard />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/seasons"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <SeasonManagementWrapper />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/schedules/constraints"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ConstraintBuilderPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/schedules/zone-mode"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ZoneSchedulingModePage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/farms/:farmId/sensors"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <FarmSensorsPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/controllers"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ControllersPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/controllers/:claimId"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ControllerCockpitPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/pump-stations/:stationId/cabezal"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <CabezalPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              {/* Farm Map Page - fullscreen, no layout wrapper (like layout editor) */}
              <Route
                path="/farms/:farmId/map"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <FarmMapPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/weather"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <WeatherPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/programs"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <ProgramListPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/irrigation-log"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <IrrigationLogPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/alerts"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <AlertsPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farms/:farmId/notifications"
                element={
                  <ProtectedRoute>
                    <FarmDashboardLayout>
                      <Suspense fallback={<LoadingFallback />}>
                        <NotificationSettingsPage />
                      </Suspense>
                    </FarmDashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback route */}
              <Route
                path="*"
                element={
                  // Wait for the session before deciding: under cookie auth this
                  // used to fire with isAuthenticated still false and send a
                  // signed-in user to /login, and from there to "/".
                  !bootstrapped ? <LoadingFallback /> :
                  isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
                }
              />
            </Routes>
          </Box>
        </Router>
        <PWAUpdatePrompt />
        <HeatmapConsentBanner />
      </PWAInstallPrompt>
      </ThemeProviderWrapper>
    </GlobalErrorBoundary>
  );
}

export default App;
